import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getCalculatorData, getFreightCost } from '../data/calculatorData';
import { AdRenderer } from '../components/SharedUI';
import { useSiteSettings } from '../hooks/useAppHooks';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { LuCalculator, LuAlertCircle, LuArrowRight, LuTruck, LuInfo } = LuIcons;

const OriginBadge = ({ origin }) => {
  if (origin === 'Europe') {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">🇪🇺 أوروبا</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">🌍 دول أخرى</span>;
};

const renderPrice = (price, energy, isHighlighted) => {
  if (energy === 'DSL') {
    return (
      <span className="text-red-500 font-bold bg-red-50 px-2 py-1 rounded-lg text-xs inline-block">
        غير مسموح
      </span>
    );
  }
  if (price === 0 || price === null || price === undefined) {
    return (
      <span className="text-slate-300 font-bold text-xs inline-block px-2 py-1">
        —
      </span>
    );
  }
  return (
    <span className={`${isHighlighted ? 'text-blue-700 text-lg font-black' : 'text-slate-800 font-black whitespace-nowrap'} transition-all duration-300`}>
      {price.toLocaleString()} <span className={`${isHighlighted ? 'text-blue-500 font-bold' : 'text-slate-400 font-normal'} text-xs ms-1`}>مليون</span>
    </span>
  );
};

const EnergyBadge = ({ energy }) => {
  const styles = {
    ESS: 'bg-green-100 text-green-800',
    HYB: 'bg-blue-100 text-blue-800',
    DSL: 'bg-red-100 text-red-800',
    ELC: 'bg-violet-100 text-violet-800',
    GAZ: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${styles[energy] || 'bg-slate-100 text-slate-700'}`}>
      {energy}
    </span>
  );
};

const ENERGY_FILTERS = [
  { key: 'all', label: 'الكل', color: 'bg-slate-600 text-white' },
  { key: 'ESS', label: 'بنزين', color: 'bg-green-100 text-green-700 hover:bg-green-200', active: 'bg-green-600 text-white' },
  { key: 'HYB', label: 'هيبرد', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', active: 'bg-blue-600 text-white' },
  { key: 'DSL', label: 'ديزل', color: 'bg-red-100 text-red-700 hover:bg-red-200', active: 'bg-red-600 text-white' },
  { key: 'ELC', label: 'كهربائي', color: 'bg-violet-100 text-violet-700 hover:bg-violet-200', active: 'bg-violet-600 text-white' },
];

const AGE_COLUMNS = [
  { key: 'all', label: 'الكل', field: null },
  { key: 'neuf', label: 'جديدة', field: 'neuf' },
  { key: 'm1', label: 'أقل من سنة', field: 'm1' },
  { key: 'm2', label: 'أقل من سنتين', field: 'm2' },
  { key: 'm3', label: 'أقل من 3 سنوات', field: 'm3' },
];

const CalculatorPage = () => {
  const { settings } = useSiteSettings();
  const [ads, setAds] = useState([]);
  const calcData = useMemo(() => getCalculatorData(), []);
  const freightCost = useMemo(() => getFreightCost(), []);
  
  const brands = useMemo(
    () => [...new Set(calcData.map(item => item.brand))].sort(),
    [calcData]
  );
  
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [energyFilter, setEnergyFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [totalCostAge, setTotalCostAge] = useState('neuf');

  const availableModels = useMemo(() => {
    if (!selectedBrand) return [];
    return [...new Set(
      calcData.filter(item => item.brand === selectedBrand).map(item => item.model)
    )].sort();
  }, [calcData, selectedBrand]);

  useEffect(() => {
    setSelectedModel('');
  }, [selectedBrand]);

  useEffect(() => {
    supabase
      .from('advertisements')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => {
        if (data) setAds(data);
      });
  }, []);

  const displayedCalcData = useMemo(() => {
    if (!selectedBrand) return [];
    let data = calcData.filter(item => item.brand === selectedBrand);
    if (selectedModel) data = data.filter(item => item.model === selectedModel);
    if (energyFilter !== 'all') data = data.filter(item => item.energy === energyFilter);
    return data;
  }, [calcData, selectedBrand, selectedModel, energyFilter]);

  const brandStats = useMemo(() => {
    if (!selectedBrand) return null;
    const items = calcData.filter(c => c.brand === selectedBrand);
    const fromEurope = items.filter(c => c.origin === 'Europe').length;
    const fromOther = items.filter(c => c.origin !== 'Europe').length;
    const models = [...new Set(items.map(c => c.model))].length;
    const energyCounts = {};
    items.forEach(c => { energyCounts[c.energy] = (energyCounts[c.energy] || 0) + 1; });
    return { total: items.length, fromEurope, fromOther, models, energyCounts };
  }, [calcData, selectedBrand]);

  const totalCostData = useMemo(() => {
    if (!selectedBrand || displayedCalcData.length === 0) return null;
    const field = AGE_COLUMNS.find(a => a.key === totalCostAge)?.field;
    if (!field) return null;
    const validItems = displayedCalcData.filter(item => item[field] && item[field] > 0 && item.energy !== 'DSL');
    if (validItems.length === 0) return null;
    const avgCustoms = validItems.reduce((sum, item) => sum + item[field], 0) / validItems.length;
    const minCustoms = Math.min(...validItems.map(item => item[field]));
    const maxCustoms = Math.max(...validItems.map(item => item[field]));
    return { avgCustoms, minCustoms, maxCustoms, count: validItems.length, field };
  }, [displayedCalcData, totalCostAge]);

  return (
    <div className="flex-1 w-full flex flex-col items-center pb-10 bg-slate-50 min-h-screen">
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        
        <AdRenderer ads={ads} location="calc_above" />

        <section className="mb-10">
          <div className="mb-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shadow-sm">
              <SafeIcon icon={LuCalculator} className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900">
                {settings.calc_title}
              </h1>
              <p className="text-slate-500 text-sm mt-1">{settings.calc_subtitle}</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Selectors */}
            <div className="p-6 bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-slate-200 flex flex-col sm:flex-row gap-5">
              <div className="flex-1">
                <label className="block text-sm font-bold text-slate-700 mb-2">اختر الماركة</label>
                <select 
                  value={selectedBrand} 
                  onChange={e => setSelectedBrand(e.target.value)}
                  className="block w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl bg-white focus:outline-none focus:border-blue-500 focus:ring-0 cursor-pointer font-bold text-slate-700 transition-colors"
                >
                  <option value="">-- اختر الماركة أولاً --</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  اختر الموديل (اختياري)
                </label>
                <select 
                  value={selectedModel} 
                  onChange={e => setSelectedModel(e.target.value)}
                  disabled={!selectedBrand}
                  className="block w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl bg-white focus:outline-none focus:border-blue-500 focus:ring-0 disabled:bg-slate-100 disabled:text-slate-400 cursor-pointer font-bold text-slate-700 transition-colors"
                >
                  <option value="">-- جميع موديلات {selectedBrand || '...'} --</option>
                  {availableModels.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Energy Filter */}
            {selectedBrand && (
              <div className="px-6 py-3 bg-white border-b border-slate-100 flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-slate-500 ms-2">الطاقة:</span>
                {ENERGY_FILTERS.map(f => {
                  const count = f.key === 'all'
                    ? calcData.filter(c => c.brand === selectedBrand && (!selectedModel || c.model === selectedModel)).length
                    : calcData.filter(c => c.brand === selectedBrand && (!selectedModel || c.model === selectedModel) && c.energy === f.key).length;
                  const isActive = energyFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setEnergyFilter(f.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${isActive ? f.active : f.color} ${count === 0 && f.key !== 'all' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                      disabled={count === 0 && f.key !== 'all'}
                    >
                      {f.label}
                      {count > 0 && <span className="ms-1 opacity-70">({count})</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Age Selector */}
            {selectedBrand && (
              <div className="px-6 py-3 bg-white border-b border-slate-100 flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-slate-500 ms-2">الفئة العمرية:</span>
                {AGE_COLUMNS.map(a => (
                  <button
                    key={a.key}
                    onClick={() => setAgeFilter(a.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      ageFilter === a.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}

            {/* Brand stats bar */}
            {brandStats && (
              <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-4 text-xs font-bold text-slate-500">
                <span>
                  <span className="text-slate-800">{brandStats.models}</span> موديل
                </span>
                <span className="text-slate-300">|</span>
                <span>
                  <span className="text-blue-600">{brandStats.fromEurope}</span> من أوروبا
                </span>
                <span className="text-slate-300">|</span>
                <span>
                  <span className="text-orange-600">{brandStats.fromOther}</span> من دول أخرى
                </span>
                <span className="text-slate-300">|</span>
                <span>
                  <span className="text-slate-800">{displayedCalcData.length}</span> نتيجة معروضة
                </span>
              </div>
            )}

            {!selectedBrand ? (
              <div className="p-16 text-center">
                <SafeIcon icon={LuArrowRight} className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-slate-600">
                  اختر الماركة من الأعلى لعرض النتائج
                </h3>
                <p className="text-sm text-slate-400 mt-2">
                  تشمل الحاسبة {brands.length} ماركة من أوروبا والدول الأخرى (الصين، اليابان، كوريا...)
                </p>
              </div>
            ) : displayedCalcData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">السيارة</th>
                      <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">المحرك / الطاقة</th>
                      <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">المصدر</th>
                      {(ageFilter === 'all' || ageFilter === 'neuf') && (
                        <th className={`px-6 py-4 text-start text-xs font-black text-slate-500 uppercase transition-all ${ageFilter === 'neuf' ? 'bg-blue-100 text-blue-700' : 'bg-blue-50/50'}`}>
                          جديدة
                        </th>
                      )}
                      {(ageFilter === 'all' || ageFilter === 'm1') && (
                        <th className={`px-6 py-4 text-start text-xs font-black text-slate-500 uppercase transition-all ${ageFilter === 'm1' ? 'bg-blue-100 text-blue-700' : ''}`}>
                          أقل من سنة
                        </th>
                      )}
                      {(ageFilter === 'all' || ageFilter === 'm2') && (
                        <th className={`px-6 py-4 text-start text-xs font-black text-slate-500 uppercase transition-all ${ageFilter === 'm2' ? 'bg-blue-100 text-blue-700' : ''}`}>
                          أقل من سنتين
                        </th>
                      )}
                      {(ageFilter === 'all' || ageFilter === 'm3') && (
                        <th className={`px-6 py-4 text-start text-xs font-black text-slate-500 uppercase transition-all ${ageFilter === 'm3' ? 'bg-blue-100 text-blue-700' : ''}`}>
                          أقل من 3 سنوات
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-50">
                    {displayedCalcData.map((item, index) => (
                      <motion.tr 
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
                        className="hover:bg-slate-50/80 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-black text-slate-900 text-sm">{item.brand}</div>
                          <div className="text-xs text-slate-500 font-medium">{item.model}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <EnergyBadge energy={item.energy} />
                          <div className="text-xs text-slate-400 mt-1.5 font-bold">
                            {item.engine !== '-' ? `${item.engine} cc` : 'كهربائي'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <OriginBadge origin={item.origin} />
                        </td>
                        {(ageFilter === 'all' || ageFilter === 'neuf') && (
                          <td className="px-6 py-4 whitespace-nowrap bg-blue-50/30">
                            {renderPrice(item.neuf, item.energy, ageFilter === 'neuf')}
                          </td>
                        )}
                        {(ageFilter === 'all' || ageFilter === 'm1') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderPrice(item.m1, item.energy, ageFilter === 'm1')}
                          </td>
                        )}
                        {(ageFilter === 'all' || ageFilter === 'm2') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderPrice(item.m2, item.energy, ageFilter === 'm2')}
                          </td>
                        )}
                        {(ageFilter === 'all' || ageFilter === 'm3') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderPrice(item.m3, item.energy, ageFilter === 'm3')}
                          </td>
                        )}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>

                {/* Total Cost Calculator */}
                {totalCostData && (
                  <div className="border-t-2 border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <SafeIcon icon={LuTruck} className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-black text-slate-900">حاسبة التكلفة الإجمالية</h3>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-sm font-bold text-slate-600">اختر الفئة العمرية:</span>
                      {AGE_COLUMNS.filter(a => a.key !== 'all').map(a => (
                        <button
                          key={a.key}
                          onClick={() => setTotalCostAge(a.key)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            totalCostAge === a.key
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                          }`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-xs font-bold text-slate-500 mb-1">متوسط الرسوم الجمركية</div>
                        <div className="text-2xl font-black text-blue-700">
                          {totalCostData.avgCustoms.toLocaleString()} <span className="text-xs font-bold text-slate-400">مليون سنتيم</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          من {totalCostData.minCustoms.toLocaleString()} إلى {totalCostData.maxCustoms.toLocaleString()} ({totalCostData.count} سيارة)
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-xs font-bold text-slate-500 mb-1">تكلفة الشحن التقديرية</div>
                        <div className="text-2xl font-black text-orange-600">
                          1,750 <span className="text-xs font-bold text-slate-400">EUR / USD</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          شاملة التأمين والنقل البحري
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-blue-200">
                        <div className="text-xs font-bold text-slate-500 mb-1">التكلفة الإجمالية التقريبية</div>
                        <div className="text-2xl font-black text-green-700">
                          {(totalCostData.avgCustoms + (1750 * 154 / 10000)).toFixed(1)} <span className="text-xs font-bold text-slate-400">مليون سنتيم</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          = {((totalCostData.avgCustoms + 1750 * 154 / 10000) * 10000 / 154).toLocaleString(undefined, {maximumFractionDigits: 0})} DZD
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                      <SafeIcon icon={LuInfo} className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                      <p>
                        الحساب: الرسوم الجمركية (مليون سنتيم) + الشحن (1750 EUR × سعر الصرف 154 / 10,000) — تقدير فقط، يختلف حسب الميناء والتقييم الفعلي.
                      </p>
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div className="p-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="text-red-500 font-bold bg-red-50 px-2 py-0.5 rounded-lg">غير مسموح</span>
                    <span>= ديزل (محظور استيراده)</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="text-slate-300 font-bold px-2 py-0.5">—</span>
                    <span>= لا تتوفر بيانات لهذه الفئة العمرية</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="font-bold text-slate-800">الأرقام</span>
                    <span>= بالمليون سنتيم</span>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border-t border-amber-100 flex items-start gap-2">
                  <SafeIcon icon={LuAlertCircle} className="h-5 w-5 flex-shrink-0 text-amber-500 mt-0.5" />
                  <p className="text-sm text-amber-800 font-medium">
                    الأسعار تقديرية بملايين السنتيمات وقد تتغير حسب التقييم الجمركي الفعلي في ميناء الوصول. مصدرين: ملف تقديرات 2026 + قيم FOB المرجعية 2025.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-10 text-center text-slate-500">
                لا توجد بيانات متاحة لهذا الاختيار.
              </div>
            )}
          </div>
        </section>

        <AdRenderer ads={ads} location="calc_below" />
      </div>
    </div>
  );
};

export default CalculatorPage;
